import { useEffect, useMemo } from 'react';
import { PhoneCallsApi } from '@frontend/api-phone-calls';
import { useAppScopeStore, useScopedQuery } from '@frontend/scope';
import { findBestEligibleCall, findBestEligibleCallWithContact } from '../utils/call-eligibility';
import { useOnboardingCallActionShallowStore } from './use-onboarding-call-action-store';

// 3 pages of the Call History table (default page size is 25).
const SEARCH_WINDOW_SIZE = 75;

interface OnboardingCallEligibilityResult {
  isLoading: boolean;
  hasEligibleCall: boolean;
  hasEligibleCallWithContact: boolean;
}

export function useOnboardingCallEligibility(): OnboardingCallEligibilityResult {
  const { selectedLocationIds } = useAppScopeStore();
  const { isEligibilityResolved, setEligibility } = useOnboardingCallActionShallowStore(
    'isEligibilityResolved',
    'setEligibility'
  );

  const { data, isLoading } = useScopedQuery({
    queryKey: ['call-intel-onboarding', 'recent-calls', SEARCH_WINDOW_SIZE],
    queryFn: async () => {
      const { records = [] } = await PhoneCallsApi.getMultiCallRecords({
        limit: SEARCH_WINDOW_SIZE,
        locationIds: selectedLocationIds,
        includeCallIntelligence: true,
      });
      return records;
    },
    enabled: selectedLocationIds.length > 0,
  });

  const { hasEligibleCall, hasEligibleCallWithContact } = useMemo(
    () => ({
      hasEligibleCall: data ? !!findBestEligibleCall(data) : false,
      hasEligibleCallWithContact: data ? !!findBestEligibleCallWithContact(data) : false,
    }),
    [data]
  );

  useEffect(() => {
    if (isLoading || !data) return;
    if (isEligibilityResolved) return;
    setEligibility({ hasEligibleCall, hasEligibleCallWithContact });
  }, [isLoading, data, isEligibilityResolved, hasEligibleCall, hasEligibleCallWithContact, setEligibility]);

  return { isLoading, hasEligibleCall, hasEligibleCallWithContact };
}
